import React, { useEffect, useState } from 'react';
import { Incident, IncidentStatus, getIncidentStatusHistory, IncidentStatusHistoryApi } from '@/services/api/incidents';

interface IncidentStatusTimelineProps {
  incident: Incident;
}

const formatTimelineDateTime = (value: string) => {
  if (!value) {
    return { date: '-', time: '' };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const parts = value.split(' ');
    return {
      date: parts.slice(0, 3).join(' ') || value,
      time: parts.slice(3).join(' '),
    };
  }

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);

  return { date, time };
};

const getStatusPillClasses = (status?: string) => {
  switch (status) {
    case 'OPEN':
    case IncidentStatus.OPEN:
      return 'bg-blue-100 text-[#335CFF]';
    case 'IN_PROGRESS':
    case IncidentStatus.IN_PROGRESS:
    case 'IN_MAINTENANCE':
      return 'bg-yellow-100 text-yellow-700';
    case 'RESOLVED':
    case IncidentStatus.RESOLVED:
    case 'MAINTENANCE_COMPLETED':
      return 'bg-green-100 text-[#1FC16B]';
    case 'CLOSED':
    case IncidentStatus.CLOSED:
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatStatusLabel = (status?: string) => {
  if (!status || typeof status !== 'string') return 'UNKNOWN';
  if (status === IncidentStatus.OPEN) return 'OPEN';
  return status.replace(/_/g, ' ');
};

const MetaRow: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="flex items-start gap-2">
    <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
      {icon}
    </span>
    <div className="flex-1 min-w-0 mt-1">{children}</div>
  </div>
);

const UserIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={`h-3 w-3 ${className}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const IncidentStatusTimeline: React.FC<IncidentStatusTimelineProps> = ({
  incident,
}) => {
  const [history, setHistory] = useState<IncidentStatusHistoryApi[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        const data = await getIncidentStatusHistory(incident.ID);
        const sortedData = [...data].sort((a, b) => 
          new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
        );
        setHistory(sortedData);
      } catch (error) {
        console.error('Failed to fetch incident status history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (incident?.ID) {
      fetchHistory();
    }
  }, [incident?.ID]);

  let timelineEntries = history.map((item, index) => {
    let text = item.reason || '';
    if (!text) {
      if (item.toStatus === 'OPEN') text = 'Case created';
      else if (item.toStatus === 'IN_MAINTENANCE') text = 'Maintenance ticket in progress. Further tracking is handled in the Maintenance module.';
      else if (item.toStatus === 'MAINTENANCE_COMPLETED') text = 'Maintenance is Completed';
    }

    return {
      id: item.ID || Math.random().toString(),
      createdAt: item.createdAt || '',
      createdBy: item.createdBy || 'System',
      status: item.toStatus || incident.status,
      actionText: text,
      metadata: item.metadata
    };
  });

  if (timelineEntries.length === 0 && !isLoading) {
    timelineEntries = [
      {
        id: incident.ID,
        createdAt: incident.createdAt || '',
        createdBy: incident.createdBy || 'System',
        status: incident.status,
        actionText: 'Case created',
        metadata: null,
      },
    ];
  }

  return (
    <div className="w-full max-w-full overflow-hidden sticky top-5 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="px-5 py-4">
        <div className="text-base font-semibold text-gray-900">
          Status Timeline
        </div>
      </div>

      <div className="bg-gray-50 px-5 py-2">
        <div className="text-xs font-medium tracking-wide text-gray-400">
          ISSUES PROGRESS
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="space-y-4">
          {isLoading ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              Loading timeline...
            </div>
          ) : timelineEntries.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
              No status history available yet.
            </div>
          ) : (
            timelineEntries.map((entry, index) => {
            const { date, time } = formatTimelineDateTime(entry.createdAt);

            const isCurrent = index === 0;
            const detailTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const iconClass = isCurrent ? 'text-black' : 'text-gray-400';
            const timeTextClass = isCurrent ? 'text-black' : 'text-gray-500';
            const statusPill = getStatusPillClasses(entry.status as string);
            const statusLabel = formatStatusLabel(entry.status as string);

            return (
              <div key={entry.id} className="flex items-start gap-3">
                <div className="flex flex-col items-center self-stretch">
                  <div
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      isCurrent
                        ? 'border-purple-400 text-purple-600'
                        : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {isCurrent ? (
                      <div className="h-2.5 w-2.5 rounded-full bg-purple-600" />
                    ) : (
                      <svg
                        className="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                  {index !== timelineEntries.length - 1 && (
                    <div className="w-px flex-1 bg-gray-200" />
                  )}
                </div>

                <div className="min-w-0 flex-1 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {date}
                        </div>
                      </div>

                      <span
                        className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusPill}`}
                      >
                        {statusLabel}
                        {entry.actionText && (
                          <span className="group relative flex items-center">
                            <i className="ri-information-line text-[10px]" />
                            <span className="pointer-events-none absolute left-1/2 bottom-full mb-1.5 hidden w-max max-w-[220px] -translate-x-1/2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] leading-tight text-gray-500 shadow-sm group-hover:block whitespace-pre-wrap text-left z-10">
                              {entry.actionText}
                            </span>
                          </span>
                        )}
                      </span>
                    </div>

                    <div className={`text-xs flex-shrink-0 ${timeTextClass}`}>
                      {time}
                    </div>
                  </div>

                  <div className={`mt-2 space-y-1.5 text-xs ${detailTextClass}`}>
                    <MetaRow icon={<UserIcon className={iconClass} />}>
                      <span className="break-all">{entry.createdBy}</span>
                    </MetaRow>
                  </div>
                </div>
              </div>
            );
          }))}
        </div>
      </div>
    </div>
  );
};

export default IncidentStatusTimeline;
